<script setup lang="ts">
import {computed} from 'vue'
import {Line} from 'vue-chartjs'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale)

const props = defineProps<{
  title: string
  labels: string[]
  datasets: {
    label: string
    data: (number | null)[]
    borderColor: string
    backgroundColor: string
  }[]
}>()

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  plugins: {
    legend: {
      display: props.datasets.length > 0,
      position: 'top' as const,
      labels: {
        boxWidth: 12,
        font: {
          size: 11,
        },
      },
    },
    title: {
      display: true,
      text: props.title,
      font: {
        size: 14,
        weight: 'normal' as const,
      },
    },
    tooltip: {
      enabled: true,
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: 'rgba(0, 0, 0, 0.05)',
      },
      ticks: {
        font: {
          size: 10,
        },
        maxRotation: 45,
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        display: true,
        color: 'rgba(0, 0, 0, 0.05)',
      },
    },
  },
  elements: {
    line: {
      tension: 0.2,
      borderWidth: 2,
    },
    point: {
      radius: 3,
      hoverRadius: 5,
    },
  },
}))

const chartData = computed(() => ({
  labels: props.labels,
  datasets: props.datasets.map((ds) => ({
    ...ds,
    fill: false,
    spanGaps: true,
  })),
}))

const hasData = computed(() => props.datasets.length > 0 && props.labels.length > 0)
</script>

<template>
  <div class="chart-container">
    <Line v-if="hasData" :data="chartData" :options="chartOptions" />
    <div v-else class="no-data">No data</div>
  </div>
</template>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-data {
  color: hsl(var(--color-40));
  font-size: 14px;
}
</style>
